import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { tokenStore } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("sv-SE", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const { companyName, logoUrl } = useBranding();

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Inbjudningslänken saknar token.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const r = await api.get(`/auth/invite/${token}`);
        setInvite(r.data);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        setError(detail || "Inbjudan är ogiltig eller har gått ut.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Lösenordet måste vara minst 8 tecken");
      return;
    }
    if (password !== confirm) {
      toast.error("Lösenorden matchar inte");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/auth/accept-invite/${token}`, { password });
      if (r.data?.access_token || r.data?.refresh_token) {
        tokenStore.set(r.data.access_token, r.data.refresh_token);
      }
      toast.success("Konto skapat — du är inloggad");
      await checkAuth();
      navigate("/", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Kunde inte slutföra registreringen";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="accept-invite-page"
      className="min-h-screen flex items-center justify-center bg-cover bg-center relative"
      style={{
        backgroundImage:
          'url("https://images.unsplash.com/photo-1591638212063-c2f69701a931?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHx3b29kd29ya2luZyUyMHN0YWlycyUyMG1vZGVybiUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODAxNDQzMDh8MA&ixlib=rb-4.1.0&q=85")',
      }}
    >
      <div className="absolute inset-0 bg-black/55" />
      <div className="relative z-10 bg-surface p-10 md:p-12 rounded-2xl max-w-md w-full mx-4 border border-app">
        {logoUrl && (
          <div className="mb-4">
            <img
              src={logoUrl}
              alt={companyName}
              className="max-h-16 max-w-[180px] object-contain"
            />
          </div>
        )}
        <div className="mb-2 text-xs tracking-[0.2em] uppercase text-text-muted">
          {companyName}
        </div>
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight text-text-primary leading-[1.05]">
          Välkommen
          <br />
          <span className="text-brand">ombord.</span>
        </h1>

        {loading ? (
          <p
            className="mt-6 text-sm text-text-muted"
            data-testid="accept-invite-loading"
          >
            Verifierar inbjudan…
          </p>
        ) : error ? (
          <div className="mt-6 space-y-4" data-testid="accept-invite-error">
            <p className="text-sm text-rust">{error}</p>
            <button
              onClick={() => navigate("/login", { replace: true })}
              className="tk-btn-outline w-full"
              data-testid="accept-invite-go-login-button"
            >
              Till inloggning
            </button>
          </div>
        ) : (
          <>
            <p className="mt-4 text-text-muted text-sm">
              Hej <strong className="text-text-primary" data-testid="accept-invite-name">{invite.name}</strong>! Välj ett lösenord nedan för att aktivera ditt konto
              {" "}<span data-testid="accept-invite-email">({invite.email})</span>.
            </p>
            <p className="mt-2 text-[11px] text-text-muted">
              Inbjudan går ut {fmtDate(invite.expires_at)}.
            </p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-4">
              <div>
                <label className="block text-xs text-text-muted mb-1.5" htmlFor="new-password">
                  Nytt lösenord (minst 8 tecken)
                </label>
                <input
                  id="new-password"
                  type="password"
                  required
                  minLength={8}
                  data-testid="accept-invite-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  className="tk-input w-full"
                  placeholder="••••••••"
                />
              </div>
              <div>
                <label className="block text-xs text-text-muted mb-1.5" htmlFor="confirm-password">
                  Bekräfta lösenord
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  required
                  minLength={8}
                  data-testid="accept-invite-confirm-input"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  className="tk-input w-full"
                  placeholder="••••••••"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="accept-invite-submit-button"
                className="tk-btn-primary w-full mt-2"
              >
                {submitting ? "Skapar konto…" : "Aktivera konto"}
              </button>
            </form>

            <p className="mt-6 text-[11px] text-text-muted">
              Har du redan ett konto?{" "}
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="underline hover:text-text-primary"
                data-testid="accept-invite-login-link"
              >
                Logga in i stället
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
